/**
 * Геометрия водоснабжения: где труба начинается, как идёт и сколько её нужно.
 *
 * Труба — ломаная по полу между двумя точками слоя сантехники: стояком, коллектором
 * или водорозеткой на стене. Концы не хранятся: точка на стене считается из стены
 * (грань, смещение), поэтому передвинул водорозетку — труба потянулась за ней.
 * Труба лежит в стяжке и у каждой точки поднимается по стене до высоты установки —
 * это зеркально кабелю, который идёт по потолку и спускается вниз.
 *
 * Правила черчения те же, что у кабеля (`snapOrthogonal`, `elbowTo`): трубу ведут
 * только под прямыми углами, как её и кладут вдоль стен.
 *
 * Все размеры — в миллиметрах.
 */
import type { Pt } from "@/shared/lib/geometry";
import type { ItemsLayer, Pipe, WallGraph } from "@/shared/plan";
import { fixturePlanPoint, polylineLength } from "./cables";

/** Точка конца трубы на плане — точка водоснабжения на грани стены. */
export const pipeEndPoint = (graph: WallGraph, plumbing: ItemsLayer, id: string): Pt | null => {
	const fixture = plumbing.fixtures[id];
	return fixture ? fixturePlanPoint(graph, fixture) : null;
};

/** Подъём от пола до точки на стене: труба идёт в стяжке и поднимается к выводу. */
export const pipeEndRise = (plumbing: ItemsLayer, id: string): number => {
	const fixture = plumbing.fixtures[id];
	return fixture ? Math.max(0, fixture.elevation) : 0;
};

/** Полная трасса трубы по плану: от точки, через повороты, к точке. */
export const pipeRoute = (graph: WallGraph, plumbing: ItemsLayer, pipe: Pipe): Pt[] | null => {
	const start = pipeEndPoint(graph, plumbing, pipe.from);
	const end = pipeEndPoint(graph, plumbing, pipe.to);
	if (!start || !end) return null;
	return [start, ...pipe.points, end];
};

/** Сколько трубы уйдёт: по полу плюс подъёмы к точкам на стенах. */
export const pipeLength = (
	graph: WallGraph,
	plumbing: ItemsLayer,
	pipe: Pipe,
): { plan: number; rises: number; total: number } | null => {
	const route = pipeRoute(graph, plumbing, pipe);
	if (!route) return null;
	const plan = polylineLength(route);
	const rises = pipeEndRise(plumbing, pipe.from) + pipeEndRise(plumbing, pipe.to);
	return { plan, rises, total: plan + rises };
};
